import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import type { ColDef, GridApi, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { graphql } from '../../gql';
import { DataGrid } from '../../components/data-grid';
import { DateInput, LoadingOverlay, PageHeader, SelectInput, displayCoordinate, displayDate, displayDecimal, displayInteger, displayPercent, displayText } from '../../components/ui';
import type { SelectOption } from '../../components/ui';

const OPTIONS_QUERY = graphql(`
  query ClockReadingSearchFilterOptions($countryId: Int, $businessLineId: Int, $regionId: Int) {
    clockReadingSearchFilterOptions(countryId: $countryId, businessLineId: $businessLineId, regionId: $regionId) {
      countries { id name } businessLines { id name } regions { id name } plants { id name }
    }
  }
`);
const SEARCH_QUERY = graphql(`
  query SearchClockReadings($input: ClockReadingSearchFilterInput!) {
    searchClockReadings(input: $input) {
      id plantId plantName assetId assetCode brandName modelName serialNumber sapOrderNumber readingDate smr runtime fuelLitres consumptionLitresPerHour
      totalFuelLitres totalIdleHours dailyIdleHours idlePercent totalPayloadTonnes dailyPayloadTonnes payloadPerCycleTonnes totalDistanceKm dailyDistanceKm
      latitude longitude altitudeMetres defRemainingTankCapacityLitres defRemainingPercent fuelRemainingTankCapacityLitres fuelRemainingPercent fuelUsedLast24HoursLitres maximumSpeedLast24 comment
    }
  }
`);

type Row = {
  id: number; plantId: number; plantName: string; assetId: number; assetCode: string;
  brandName?: string | null; modelName?: string | null; serialNumber?: string | null; sapOrderNumber?: string | null;
  readingDate: string; smr: number; runtime?: number | null; fuelLitres?: number | null; consumptionLitresPerHour?: number | null;
  totalFuelLitres?: number | null; totalIdleHours?: number | null; dailyIdleHours?: number | null; idlePercent?: number | null;
  totalPayloadTonnes?: number | null; dailyPayloadTonnes?: number | null; payloadPerCycleTonnes?: number | null; totalDistanceKm?: number | null; dailyDistanceKm?: number | null;
  latitude?: number | null; longitude?: number | null; altitudeMetres?: number | null; defRemainingTankCapacityLitres?: number | null; defRemainingPercent?: number | null;
  fuelRemainingTankCapacityLitres?: number | null; fuelRemainingPercent?: number | null; fuelUsedLast24HoursLitres?: number | null; maximumSpeedLast24?: number | null; comment?: string | null;
};
type Draft = { countryId: string; businessLineId: string; regionId: string; plantId: string; fromDate: string; toDate: string };

const LITRES_PER_US_GALLON = 3.785411784;
const defaultColumns = ['fuelLitres', 'consumptionLitresPerHour', 'comment'];

function priorMonth(): Pick<Draft, 'fromDate' | 'toDate'> {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  const first = new Date(last.getFullYear(), last.getMonth(), 1);
  const format = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return { fromDate: format(first), toDate: format(last) };
}
function id(value: string | null): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}
function options(all: string, values: readonly { id: number; name: string }[]): SelectOption[] {
  return [{ value: '', label: all }, ...values.map((value) => ({ value: value.id, label: value.name }))];
}
function displayConvertedFuel(value: unknown, showGallons: boolean): string {
  const litres = typeof value === 'number' ? value : null;
  return displayDecimal(litres === null || !showGallons ? litres : litres / LITRES_PER_US_GALLON);
}
function createColumns(showGallons: boolean, selectedColumns: string[]): ColDef<Row>[] {
  const fuelUnit = showGallons ? 'US gal' : 'L';
  const optional = (field: keyof Row, headerName: string, valueFormatter: (params: ValueFormatterParams<Row>) => string): ColDef<Row> => ({
    field,
    headerName,
    hide: !selectedColumns.includes(field),
    valueFormatter,
  });
  const fuel = (params: ValueFormatterParams<Row>) => displayConvertedFuel(params.value, showGallons);

  return [
    { field: 'plantName', headerName: 'Plant', flex: 1, cellRenderer: (cell: ICellRendererParams<Row>) => <Link to={`/plants/${cell.data?.plantId}`}>{cell.value}</Link> },
    { field: 'assetCode', headerName: 'Asset Code', flex: 1, cellRenderer: (cell: ICellRendererParams<Row>) => <Link to={`/assets/${cell.data?.assetId}`}>{cell.value}</Link> },
    { field: 'brandName', headerName: 'Brand', flex: 1 }, { field: 'modelName', headerName: 'Model', flex: 1 }, { field: 'serialNumber', headerName: 'Serial Number', flex: 1 },
    { field: 'sapOrderNumber', headerName: 'SAP Number', flex: 1 }, { field: 'readingDate', headerName: 'Reading Date', flex: 1, valueFormatter: (cell: ValueFormatterParams<Row>) => displayDate(cell.value) },
    { field: 'smr', headerName: 'SMR', flex: 1, valueFormatter: (cell: ValueFormatterParams<Row>) => displayInteger(cell.value) }, { field: 'runtime', headerName: 'Runtime [h]', flex: 1, valueFormatter: (cell: ValueFormatterParams<Row>) => displayInteger(cell.value) },
    optional('fuelLitres', `Fuel [${fuelUnit}]`, fuel), optional('consumptionLitresPerHour', `Fuel Burn [${fuelUnit}/h]`, fuel),
    optional('totalFuelLitres', `Total Fuel [${fuelUnit}]`, fuel), optional('totalIdleHours', 'Total Idle [h]', (cell) => displayDecimal(cell.value as number | null | undefined)),
    optional('dailyIdleHours', 'Daily Idle [h]', (cell) => displayDecimal(cell.value as number | null | undefined)), optional('idlePercent', 'Idle [%]', (cell) => displayPercent(cell.value as number | null | undefined)),
    optional('totalPayloadTonnes', 'Total Payload [t]', (cell) => displayDecimal(cell.value as number | null | undefined)), optional('dailyPayloadTonnes', 'Daily Payload [t]', (cell) => displayDecimal(cell.value as number | null | undefined)),
    optional('payloadPerCycleTonnes', 'Payload per Cycle [t]', (cell) => displayDecimal(cell.value as number | null | undefined)), optional('totalDistanceKm', 'Total Distance [km]', (cell) => displayDecimal(cell.value as number | null | undefined)),
    optional('dailyDistanceKm', 'Daily Distance [km]', (cell) => displayDecimal(cell.value as number | null | undefined)), optional('latitude', 'Latitude', (cell) => displayCoordinate(cell.value as number | null | undefined)),
    optional('longitude', 'Longitude', (cell) => displayCoordinate(cell.value as number | null | undefined)), optional('altitudeMetres', 'Altitude [m]', (cell) => displayDecimal(cell.value as number | null | undefined)),
    optional('defRemainingTankCapacityLitres', `DEF Remaining Tank [${fuelUnit}]`, fuel), optional('defRemainingPercent', 'DEF Remaining [%]', (cell) => displayPercent(cell.value as number | null | undefined)),
    optional('fuelRemainingTankCapacityLitres', `Fuel Remaining Tank [${fuelUnit}]`, fuel), optional('fuelRemainingPercent', 'Fuel Remaining [%]', (cell) => displayPercent(cell.value as number | null | undefined)),
    optional('fuelUsedLast24HoursLitres', `Fuel Used Last 24h [${fuelUnit}]`, fuel), optional('maximumSpeedLast24', 'MAX Speed Last 24h', (cell) => displayDecimal(cell.value as number | null | undefined)),
    optional('comment', 'Comment', (cell) => displayText(cell.value as string | null | undefined)),
  ];
}

/** Global, read-only Clock Reading history for Plants the user can access. */
export function ClockReadingSearchPage() {
  const [params, setParams] = useSearchParams();
  const period = useMemo(priorMonth, []);
  const applied = params.get('applied') === 'true';
  const appliedInput = useMemo(() => ({
    countryId: id(params.get('country')), businessLineId: id(params.get('businessLine')), regionId: id(params.get('region')), plantId: id(params.get('plant')),
    fromDate: params.get('from') ?? period.fromDate, toDate: params.get('to') ?? period.toDate,
  }), [params, period]);
  const [draft, setDraft] = useState<Draft>({ countryId: '', businessLineId: '', regionId: '', plantId: '', ...period });
  const [showGallons, setShowGallons] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
  const gridApi = useRef<GridApi | null>(null);
  useEffect(() => setDraft({
    countryId: String(appliedInput.countryId ?? ''), businessLineId: String(appliedInput.businessLineId ?? ''),
    regionId: String(appliedInput.regionId ?? ''), plantId: String(appliedInput.plantId ?? ''), fromDate: appliedInput.fromDate, toDate: appliedInput.toDate,
  }), [appliedInput]);
  const invalidRange = !draft.fromDate || !draft.toDate || draft.fromDate > draft.toDate;
  const { data: optionData, loading: optionsLoading } = useQuery(OPTIONS_QUERY, { variables: { countryId: id(draft.countryId), businessLineId: id(draft.businessLineId), regionId: id(draft.regionId) } });
  const { data, loading, error } = useQuery(SEARCH_QUERY, {
    variables: { input: appliedInput },
    skip: !applied,
  });
  const rows = (data?.searchClockReadings ?? []) as Row[];
  const filterOptions = optionData?.clockReadingSearchFilterOptions;
  const change = (key: keyof Draft, value: string, children: (keyof Draft)[] = []) => setDraft((current) => ({ ...current, [key]: value, ...Object.fromEntries(children.map((child) => [child, ''])) }));
  const filter = () => {
    if (invalidRange) return;
    const next = new URLSearchParams({ applied: 'true', from: draft.fromDate, to: draft.toDate });
    if (draft.countryId) next.set('country', draft.countryId);
    if (draft.businessLineId) next.set('businessLine', draft.businessLineId);
    if (draft.regionId) next.set('region', draft.regionId);
    if (draft.plantId) next.set('plant', draft.plantId);
    setParams(next);
  };
  const clear = () => { setDraft({ countryId: '', businessLineId: '', regionId: '', plantId: '', ...period }); gridApi.current?.setFilterModel(null); setParams({}); };
  const columns = useMemo(() => createColumns(showGallons, selectedColumns), [showGallons, selectedColumns]);
  const optionalColumnFields = columns.slice(9).map((column) => column.field as string);
  const allColumnsSelected = optionalColumnFields.every((field) => selectedColumns.includes(field));
  const toggleColumn = (field: string) => setSelectedColumns((selected) => selected.includes(field)
    ? selected.filter((selectedField) => selectedField !== field)
    : [...selected, field]);
  const toggleAllColumns = () => setSelectedColumns(allColumnsSelected ? [] : optionalColumnFields);
  return <main className="max-w-[1440px] mx-auto px-6 py-8">
    <LoadingOverlay visible={optionsLoading || loading} />
    <PageHeader title="Clock Readings" />
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <SelectInput label="Country" fullWidth={false} sx={{ minWidth: 160 }} value={draft.countryId} onChange={(event) => change('countryId', String(event.target.value), ['businessLineId', 'regionId', 'plantId'])} options={options('All Countries', filterOptions?.countries ?? [])} />
      <SelectInput label="Business Line" fullWidth={false} sx={{ minWidth: 160 }} value={draft.businessLineId} onChange={(event) => change('businessLineId', String(event.target.value), ['regionId', 'plantId'])} options={options('All Business Lines', filterOptions?.businessLines ?? [])} />
      <SelectInput label="Region" fullWidth={false} sx={{ minWidth: 160 }} value={draft.regionId} onChange={(event) => change('regionId', String(event.target.value), ['plantId'])} options={options('All Regions', filterOptions?.regions ?? [])} />
      <SelectInput label="Plant" fullWidth={false} sx={{ minWidth: 180 }} value={draft.plantId} onChange={(event) => change('plantId', String(event.target.value))} options={options('All Plants', filterOptions?.plants ?? [])} />
      <DateInput label="From" value={draft.fromDate} onChange={(value) => change('fromDate', value ?? '')} /><DateInput label="To" value={draft.toDate} onChange={(value) => change('toDate', value ?? '')} />
      <Button variant="outlined" onClick={clear}>Clear</Button><Button variant="contained" onClick={filter} disabled={invalidRange}>Filter</Button>
      <FormControlLabel control={<Switch checked={showGallons} onChange={(_, checked) => setShowGallons(checked)} />} label="Show values in US gallons" />
      <Button aria-haspopup="menu" aria-label="Select Clock Reading columns" onClick={(event) => setColumnMenuAnchor(event.currentTarget)}>Columns</Button>
      <Menu
        anchorEl={columnMenuAnchor}
        open={columnMenuAnchor !== null}
        onClose={() => setColumnMenuAnchor(null)}
        slotProps={{
          paper: {
            sx: {
              height: 360,
              maxHeight: 360,
              overflowY: 'auto',
              backgroundColor: 'var(--container_background_elevated)',
              border: '1px solid var(--border)',
              color: 'var(--foreground)',
              '& .MuiMenuItem-root': {
                color: 'var(--foreground)',
                '&:hover': { backgroundColor: 'var(--container_background_hover)' },
                '&.Mui-selected': { backgroundColor: 'var(--container_background_selected)' },
              },
              '& .MuiCheckbox-root': { color: 'var(--foreground_faded)' },
              '& .MuiCheckbox-root.Mui-checked': { color: 'var(--primary)' },
            },
          },
        }}
      >
        <MenuItem role="menuitemcheckbox" aria-checked={allColumnsSelected} onClick={toggleAllColumns}>
          <Checkbox checked={allColumnsSelected} />Select all
        </MenuItem>
        {columns.slice(9).map((column) => (
          <MenuItem key={column.field} role="menuitemcheckbox" aria-checked={selectedColumns.includes(column.field as string)} onClick={() => toggleColumn(column.field as string)}>
            <Checkbox checked={selectedColumns.includes(column.field as string)} />{column.headerName}
          </MenuItem>
        ))}
      </Menu>
    </div>
    {invalidRange && <p role="alert" className="text-negative">End date must be on or after start date.</p>}
    {error && <p role="alert" className="text-negative">{error.message}</p>}
    {applied && !loading && rows.length === 0 && <p className="text-foreground-faded">No clock readings match the current filters.</p>}
    {rows.length > 0 && <DataGrid rowData={rows} columnDefs={columns} onGridReady={(event) => { gridApi.current = event.api; }} height="70vh" />}
  </main>;
}
